import { type FC } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { YearInput } from "@/shared/components/ui/YearInput";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type {
  EarlierMarriageRow,
  MarriedAtDeathAnswer,
  HasEarlierMarriagesAnswer,
} from "@/shared/types/probateTypes";

import { EarlierMarriagesFields, createEmptyMarriage } from "./EarlierMarriagesFields";
import { NOT_SURE_CAPTION } from "./nameUtils";
import { useJsonListAnswer } from "./useJsonListAnswer";

const MARRIED_KEY = "family_triage_married_at_death";
const SPOUSE_FIRST_KEY = "family_triage_spouse_first_name";
const SPOUSE_MIDDLE_KEY = "family_triage_spouse_middle_name";
const SPOUSE_LAST_KEY = "family_triage_spouse_last_name";
const SPOUSE_STATUS_KEY = "family_triage_spouse_status";
const SPOUSE_DEATH_YEAR_KEY = "family_triage_spouse_death_year";
const EARLIER_KEY = "family_triage_earlier_marriages";
const EARLIER_LIST_KEY = "family_triage_earlier_marriages_list";

export const MarriagesScreen: FC<FlowCustomComponentProps> = ({ answers, setAnswer, errors }) => {
  const { person } = useAppContext();
  const name = person?.first_name ?? "the decedent";

  const marriedAtDeath = (answers[MARRIED_KEY] as MarriedAtDeathAnswer) ?? "";
  const spouseFirst = (answers[SPOUSE_FIRST_KEY] as string) ?? "";
  const spouseMiddle = (answers[SPOUSE_MIDDLE_KEY] as string) ?? "";
  const spouseLast = (answers[SPOUSE_LAST_KEY] as string) ?? "";
  const spouseStatus = (answers[SPOUSE_STATUS_KEY] as string) ?? "";
  const spouseDeathYear = (answers[SPOUSE_DEATH_YEAR_KEY] as string) ?? "";
  const earlierAnswer = (answers[EARLIER_KEY] as HasEarlierMarriagesAnswer) ?? "";

  const [marriages, syncMarriages] = useJsonListAnswer<EarlierMarriageRow>(
    answers,
    EARLIER_LIST_KEY,
    setAnswer,
  );

  const handleMarriedSelect = (value: string) => {
    setAnswer(MARRIED_KEY, value);
    if (value !== "yes") {
      setAnswer(SPOUSE_FIRST_KEY, "");
      setAnswer(SPOUSE_MIDDLE_KEY, "");
      setAnswer(SPOUSE_LAST_KEY, "");
      setAnswer(SPOUSE_STATUS_KEY, "");
      setAnswer(SPOUSE_DEATH_YEAR_KEY, "");
    }
  };

  const handleSpouseStatusChange = (value: string) => {
    setAnswer(SPOUSE_STATUS_KEY, value);
    if (value !== "passed_away") {
      setAnswer(SPOUSE_DEATH_YEAR_KEY, "");
    }
  };

  const handleEarlierSelect = (value: string) => {
    const val = value as HasEarlierMarriagesAnswer;
    setAnswer(EARLIER_KEY, val);
    if (val === "yes" && marriages.length === 0) {
      syncMarriages([createEmptyMarriage()]);
    } else if (val !== "yes") {
      syncMarriages([]);
    }
  };

  const updateMarriage = (index: number, field: keyof EarlierMarriageRow, value: string) => {
    const next = [...marriages];
    next[index] = { ...next[index], [field]: value };
    syncMarriages(next);
  };

  const addMarriage = () => {
    syncMarriages([...marriages, createEmptyMarriage()]);
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Spouse section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            Was {name} married when they passed away?
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            Marriage determines who inherits and who has the right to lead the estate. This helps us
            customize the exact forms your local court will require.
          </p>
        </div>

        <RadioGroup
          name="married_at_death"
          value={marriedAtDeath}
          onValueChange={handleMarriedSelect}
          className="flex flex-col gap-3"
        >
          <OptionCard
            label={`Yes, ${name} was married`}
            selected={marriedAtDeath === "yes"}
            onClick={() => handleMarriedSelect("yes")}
            variant="radio"
            value="yes"
          />
          <OptionCard
            label={`No, ${name} wasn't married`}
            selected={marriedAtDeath === "no"}
            onClick={() => handleMarriedSelect("no")}
            variant="radio"
            value="no"
          />
        </RadioGroup>
        <FieldError message={errors?.married_at_death} />

        {marriedAtDeath === "yes" && (
          <div className="flex flex-col gap-4">
            <div className="border-t border-primary" />

            <p className="font-sans text-xl font-semibold leading-[30px]">
              Tell us about {name}'s spouse
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>First name</Label>
                <Input
                  placeholder="First name"
                  value={spouseFirst}
                  onChange={(e) => setAnswer(SPOUSE_FIRST_KEY, e.target.value)}
                  className={errors?.spouse_first_name ? "border-destructive" : ""}
                />
                <FieldError message={errors?.spouse_first_name} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>
                  Middle name <span className="text-tertiary">(optional)</span>
                </Label>
                <Input
                  placeholder="Middle name"
                  value={spouseMiddle}
                  onChange={(e) => setAnswer(SPOUSE_MIDDLE_KEY, e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Last name</Label>
              <Input
                placeholder="Last name"
                value={spouseLast}
                onChange={(e) => setAnswer(SPOUSE_LAST_KEY, e.target.value)}
                className={errors?.spouse_last_name ? "border-destructive" : ""}
              />
              <FieldError message={errors?.spouse_last_name} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select value={spouseStatus} onValueChange={handleSpouseStatusChange}>
                <SelectTrigger className={errors?.spouse_status ? "border-destructive" : ""}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="living">Living</SelectItem>
                  <SelectItem value="passed_away">Passed away</SelectItem>
                  <SelectItem value="not_sure">Not sure</SelectItem>
                </SelectContent>
              </Select>
              <FieldError message={errors?.spouse_status} />
            </div>

            {spouseStatus === "passed_away" && (
              <div className="flex flex-col gap-1.5">
                <Label>What year did they pass?</Label>
                <YearInput
                  value={spouseDeathYear}
                  onChange={(v) => setAnswer(SPOUSE_DEATH_YEAR_KEY, v)}
                />
                <FieldError message={errors?.spouse_death_year} />
                <p className="text-xs text-secondary">
                  A best guess is fine, and it can be left blank.
                </p>
              </div>
            )}

            {spouseStatus === "not_sure" && (
              <p className="text-xs text-secondary">{NOT_SURE_CAPTION}</p>
            )}
          </div>
        )}
      </div>

      {/* Earlier marriages section -- shows once marriage question is answered */}
      {marriedAtDeath && (
        <div className="flex flex-col gap-4">
          <div className="border-t border-primary" />

          <div className="flex flex-col gap-1">
            <p className="font-sans text-xl font-semibold leading-[30px]">
              Was {name} ever divorced or married before?
            </p>
            <p className="text-sm text-secondary tracking-[-0.28px]">
              Divorces and earlier marriages change who receives formal court notices, even if the
              will is newer.
            </p>
          </div>

          <RadioGroup
            name="earlier_marriages"
            value={earlierAnswer}
            onValueChange={handleEarlierSelect}
            className="flex flex-col gap-3"
          >
            <OptionCard
              label="Yes"
              selected={earlierAnswer === "yes"}
              onClick={() => handleEarlierSelect("yes")}
              variant="radio"
              value="yes"
            />
            <OptionCard
              label={`No, ${name} wasn't married before`}
              selected={earlierAnswer === "no"}
              onClick={() => handleEarlierSelect("no")}
              variant="radio"
              value="no"
            />
          </RadioGroup>
          <FieldError message={errors?.earlier_marriages} />

          {earlierAnswer === "yes" && (
            <div className="flex flex-col gap-4">
              <p className="font-sans text-xl font-semibold leading-[30px]">
                Tell us about {name}'s earlier marriages
              </p>

              <EarlierMarriagesFields
                marriages={marriages}
                onChange={updateMarriage}
                onAdd={addMarriage}
                errors={errors}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
